import axios from "axios";

let BASE_URL = "/api";
if (import.meta.env.DEV) {
  BASE_URL = "http://localhost:4500/api";
}

const AUTH_CONTRACT_HEADERS = {
  "X-Mysolon-Auth-Contract": "2",
  "X-CSRF-Protection": "1",
};

const api = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
  headers: AUTH_CONTRACT_HEADERS,
});

export const axiosPrivate = axios.create({
  baseURL: BASE_URL,
  headers: {
    "Content-Type": "application/json",
    ...AUTH_CONTRACT_HEADERS,
  },
  withCredentials: true,
});

let currentAccessToken = null;
let updateAuthState = null;
let refreshPromise = null;

const LOCK_KEY = "rf_lock_v2";
const LAST_KEY = "rf_last_v2";
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const safeGet = (key) => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeSet = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch {}
};

const tryAcquireLock = (id, ttlMs) => {
  try {
    const raw = localStorage.getItem(LOCK_KEY);
    if (raw) {
      const lock = JSON.parse(raw);
      if (lock?.timestamp + ttlMs > Date.now()) return false;
    }
    localStorage.setItem(
      LOCK_KEY,
      JSON.stringify({ id, timestamp: Date.now() })
    );
    return true;
  } catch {
    return true;
  }
};

const releaseLock = (id) => {
  try {
    const raw = localStorage.getItem(LOCK_KEY);
    if (raw && JSON.parse(raw)?.id === id) localStorage.removeItem(LOCK_KEY);
  } catch {}
};

export const configureAuthSession = (accessToken, setAuth) => {
  currentAccessToken = accessToken || null;
  updateAuthState = setAuth;
};

const applySession = (data) => {
  currentAccessToken = data.accessToken;
  updateAuthState?.((previous) => ({
    ...previous,
    accessToken: data.accessToken,
    user: data.email,
    role: data.role,
    uuid: data.uuid,
    avatar: data.avatar,
    id: data.id,
  }));
  safeSet(LAST_KEY, String(Date.now()));
  return data.accessToken;
};

export const clearAuthSession = () => {
  currentAccessToken = null;
  updateAuthState?.({});
};

export const refreshAccessToken = async () => {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    const lockId = `${Date.now()}_${Math.random().toString(36).slice(2)}`;
    const startedAt = Date.now();
    while (!tryAcquireLock(lockId, 4_000)) {
      if (Date.now() - startedAt >= 5_000) break;
      await sleep(150);
    }

    const lastRefresh = Number(safeGet(LAST_KEY) || 0);
    if (lastRefresh && Date.now() - lastRefresh < 350) {
      await sleep(350 - (Date.now() - lastRefresh));
    }

    try {
      try {
        const response = await api.post("/refresh", null, {
          headers: AUTH_CONTRACT_HEADERS,
          _skipAuthRefresh: true,
        });
        return applySession(response.data);
      } catch (error) {
        if (
          error?.response?.status === 409 &&
          error?.response?.data?.code === "refresh_race"
        ) {
          await sleep(300);
          const retry = await api.post("/refresh", null, {
            headers: AUTH_CONTRACT_HEADERS,
            _skipAuthRefresh: true,
          });
          return applySession(retry.data);
        }
        if (
          [
            "refresh_token_missing",
            "refresh_token_invalid",
            "refresh_token_expired",
            "refresh_token_reused",
          ].includes(error?.response?.data?.code)
        ) {
          clearAuthSession();
        }
        throw error;
      }
    } finally {
      releaseLock(lockId);
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

axiosPrivate.interceptors.request.use((config) => {
  if (!config.headers.Authorization && currentAccessToken) {
    config.headers.Authorization = `Bearer ${currentAccessToken}`;
  }
  return config;
});

axiosPrivate.interceptors.response.use(
  (response) => response,
  async (error) => {
    const request = error?.config;
    const code = error?.response?.data?.code;
    if (
      request &&
      !request._skipAuthRefresh &&
      !request._authRetried &&
      code === "access_token_expired"
    ) {
      request._authRetried = true;
      const accessToken = await refreshAccessToken();
      request.headers.Authorization = `Bearer ${accessToken}`;
      return axiosPrivate(request);
    }
    if (
      [
        "session_revoked",
        "refresh_token_invalid",
        "refresh_token_expired",
        "refresh_token_reused",
      ].includes(code)
    ) {
      clearAuthSession();
    }
    return Promise.reject(error);
  }
);

export default api;
